import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Star, Copy, ExternalLink } from "lucide-react";
import { getScoreColor } from "@/lib/scoreUtils";
import { RemixButton } from "@/components/feed/RemixButton";
import { useToggleFavorite, useCheckFavorited } from "@/hooks/useFavorites";
import { cn } from "@/lib/utils";
import { PromptDetailModal } from "@/components/shared/PromptDetailModal";

interface PromptOfDay {
  score_id: string;
  submission_id: string;
  score_total: number;
  detected_type: string;
  best_prompt: string | null;
  user_name: string;
  user_initials: string;
  created_at: string;
  evaluation_markdown?: string;
  favorite_count: number;
  tags: string[];
}

const CATEGORY_COLORS: Record<string, string> = {
  'Planning': 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
  'Strategy': 'bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/30',
  'Research': 'bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30',
  'Drafting': 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30',
  'Analysis': 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',
  'Creative': 'bg-pink-500/15 text-pink-700 dark:text-pink-300 border-pink-500/30',
  'Brainstorming': 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-300 border-yellow-500/30',
  'Summarizing': 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
  'Unknown': 'bg-muted text-muted-foreground border-border',
};

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function getPromptSummary(pod: PromptOfDay): string {
  if (pod.evaluation_markdown) {
    try {
      const parsed = JSON.parse(pod.evaluation_markdown);
      if (parsed.prompt_summary) return parsed.prompt_summary;
    } catch {}
  }
  return 'Provides structured guidance for your task';
}

function getPromptTitle(pod: PromptOfDay): string | null {
  if (pod.evaluation_markdown) {
    try {
      const parsed = JSON.parse(pod.evaluation_markdown);
      if (parsed.prompt_title) return parsed.prompt_title;
    } catch {}
  }
  return null;
}

interface FeedPromptOfDayProps {
  /** "card" = mobile injected card (default), "compact" = right rail compact */
  variant?: "card" | "compact";
}

export function FeedPromptOfDay({ variant = "card" }: FeedPromptOfDayProps) {
  const { user, membership } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [promptOfDay, setPromptOfDay] = useState<PromptOfDay | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<any>(null);
  const [modalLoading, setModalLoading] = useState(false);

  const toggleFavoriteMutation = useToggleFavorite();
  const { data: isPodFavorited, isLoading: isPodFavLoading } = useCheckFavorited(
    user?.id, 
    promptOfDay?.score_id || ''
  );

  useEffect(() => {
    if (user && membership?.org_id) {
      fetchPromptOfDay();
    }
  }, [user, membership?.org_id]);

  const fetchPromptOfDay = async () => {
    if (!membership?.org_id) return;
    setLoading(true);

    try {
      const { data, error } = await supabase.rpc('get_org_prompt_of_day', { 
        _org_id: membership.org_id 
      });

      if (error) {
        console.error("Error fetching prompt of day:", error);
        return;
      }

      if (data && data.length > 0) {
        setPromptOfDay(data[0] as PromptOfDay);
      }
    } catch (error) {
      console.error("Error fetching prompt of day:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFavoriteToggle = () => {
    if (!user || !promptOfDay) {
      toast.error('Please sign in to save favorites');
      return;
    }
    toggleFavoriteMutation.mutate({ 
      userId: user.id, 
      scoreId: promptOfDay.score_id, 
      isFavorited: !!isPodFavorited 
    });
  };

  const handleCopy = async () => {
    if (!promptOfDay?.best_prompt) return;
    try {
      await navigator.clipboard.writeText(promptOfDay.best_prompt);
      toast.success('Prompt copied to clipboard');
      if (user) {
        await supabase.from('prompt_actions').insert({
          score_id: promptOfDay.score_id,
          user_id: user.id,
          action_type: 'copy',
        });
      }
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleShare = async () => {
    if (!user || !promptOfDay) return;
    try {
      await supabase.from('prompt_actions').insert({
        score_id: promptOfDay.score_id,
        user_id: user.id,
        action_type: 'share_chatgpt',
      });
    } catch (err) {
      console.error('Failed to track share:', err);
    }
  };

  const handleRemix = () => {
    if (!promptOfDay?.best_prompt) return;
    navigate('/prompt', {
      state: {
        remixContext: {
          originalPrompt: promptOfDay.best_prompt,
          originalScoreId: promptOfDay.score_id,
        }
      }
    });
  };

  const handleOpenModal = async () => {
    if (!promptOfDay || !membership?.org_id) return;
    
    setModalOpen(true);
    setModalLoading(true);
    
    try {
      const { data, error } = await supabase.rpc('get_prompt_detail_for_org', {
        _org_id: membership.org_id,
        _score_id: promptOfDay.score_id,
      });
      
      if (error) throw error;
      if (data && data.length > 0) {
        setModalData(data[0]);
      }
    } catch (err) {
      console.error('Failed to load prompt details:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleModalRemix = (data: any) => {
    if (!data?.best_prompt_extracted) return;
    setModalOpen(false);
    navigate('/prompt', {
      state: {
        remixContext: {
          originalPrompt: data.best_prompt_extracted,
          originalScoreId: data.score_id,
        }
      }
    });
  };

  if (loading) {
    return (
      <div className={variant === "compact" ? "" : "p-4 md:p-5"}>
        <Skeleton className={variant === "compact" ? "h-48 w-full" : "h-48 w-full"} />
      </div>
    );
  }

  if (!promptOfDay) {
    if (variant === "compact") {
      return (
        <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Star className="h-4 w-4 text-yellow-500" />
              Prompt of the Day
            </CardTitle>
            <CardDescription className="text-xs">Top prompt in the last 24h</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-xs">No submissions in the last 24 hours.</p>
          </CardContent>
        </Card>
      );
    }
    return null;
  }

  const isCompact = variant === "compact";
  const iconSize = isCompact ? "h-3.5 w-3.5" : "h-4 w-4";
  const btnSize = isCompact ? "h-7" : "h-8";
  const btnIconSize = isCompact ? "h-7 w-7" : "h-8 w-8";

  const scoreRow = (
    <div className="flex items-center justify-between">
      <div className="flex items-baseline gap-1.5 flex-shrink-0">
        <span className={`font-bold ${isCompact ? 'text-lg' : 'text-xl'} ${getScoreColor(promptOfDay.score_total)}`}>
          {promptOfDay.score_total}
        </span>
        <span className={`${isCompact ? 'text-xs' : 'text-sm'} text-muted-foreground/60`}>/100</span>
        <span className={`${isCompact ? 'text-xs' : 'text-xs'} text-muted-foreground ml-0.5`}>Prompt Score</span>
      </div>
      <Badge 
        variant="outline" 
        className={`text-xs border flex-shrink-0 rounded-md ${CATEGORY_COLORS[promptOfDay.detected_type] || CATEGORY_COLORS['Unknown']}`}
      >
        {promptOfDay.detected_type || 'Unknown'}
      </Badge>
    </div>
  );

  const actionsRow = (
    <div className={`flex items-center justify-between ${isCompact ? 'pt-1' : ''}`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-0.5">
        <Button 
          variant="ghost" 
          size="sm" 
          className={cn(`${btnSize} px-1.5 gap-1`, isPodFavLoading && "opacity-50")}
          onClick={handleFavoriteToggle}
          disabled={toggleFavoriteMutation.isPending || isPodFavLoading}
          title={isPodFavorited ? 'Remove from favorites' : 'Add to favorites'}
        >
          <Star className={cn(
            iconSize,
            isPodFavorited 
              ? "fill-yellow-500 text-yellow-500" 
              : "text-muted-foreground"
          )} />
          {promptOfDay.favorite_count > 0 && (
            <span className="text-xs text-muted-foreground">{promptOfDay.favorite_count}</span>
          )}
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`${btnIconSize} p-0`}
          onClick={handleCopy}
          title="Copy prompt"
        >
          <Copy className={iconSize} />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`${btnIconSize} p-0`}
          asChild
          title="Try in ChatGPT"
          onClick={handleShare}
        >
          <a 
            href={promptOfDay.best_prompt ? `https://chatgpt.com/?prompt=${encodeURIComponent(promptOfDay.best_prompt)}` : '#'} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <ExternalLink className={iconSize} />
          </a>
        </Button>
      </div>
      <RemixButton onClick={handleRemix} />
    </div>
  );

  const content = (
    <div className={`space-y-${isCompact ? '2' : '3'}`}>
      {/* Score + "Prompt Score" label + Category */}
      {scoreRow}

      {/* Title + Summary */}
      <div>
        {getPromptTitle(promptOfDay) && (
          <h3 className={`${isCompact ? 'text-xs' : 'text-[17px]'} font-semibold text-foreground mb-1`}>
            {getPromptTitle(promptOfDay)}
          </h3>
        )}
        <p className={`${isCompact ? 'text-xs' : 'text-[15px]'} text-foreground leading-relaxed ${isCompact ? 'line-clamp-2' : ''}`}>
          {getPromptSummary(promptOfDay)}
        </p>
      </div>

      {/* Actions */}
      {actionsRow}
    </div>
  );

  // Compact variant: used in the right rail
  if (isCompact) {
    return (
      <>
        <Card 
          className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 cursor-pointer hover:bg-primary/15 transition-colors"
          onClick={handleOpenModal}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Star className="h-4 w-4 text-yellow-500" />
                Prompt of the Day
              </CardTitle>
              <span className="text-xs text-muted-foreground">
                {formatRelativeTime(promptOfDay.created_at)}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {content}
          </CardContent>
        </Card>
        <PromptDetailModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          data={modalData}
          loading={modalLoading}
          showUserName={false}
          onRemix={handleModalRemix}
        />
      </>
    );
  }

  // Card variant: injected into feed on mobile/tablet
  return (
    <div className="p-4 md:p-5">
      <Card 
        className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 min-w-0 cursor-pointer hover:bg-primary/15 transition-colors"
        onClick={handleOpenModal}
      >
        <CardContent className="p-4">
          <div className="space-y-3">
            {/* Header: icon + label (left), timestamp (right) */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[15px] font-medium text-primary">
                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                <span>Prompt of the Day</span>
              </div>
              <span className="text-xs text-muted-foreground">
                {formatRelativeTime(promptOfDay.created_at)}
              </span>
            </div>

            {content}
          </div>
        </CardContent>
      </Card>
      
      <PromptDetailModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        data={modalData}
        loading={modalLoading}
        showUserName={false}
        onRemix={handleModalRemix}
      />
    </div>
  );
}
